import * as XLSX from 'xlsx';

type SheetRow = Record<string, unknown>;

export type ImportedFracturePlan = {
  design: Record<string, unknown>;
  stages: Array<Record<string, unknown>>;
  warnings: string[];
};

const normalize = (value: unknown) => String(value ?? '')
  .trim().toLowerCase().replace(/³/g, '3').replace(/[（）()\[\]{}\s_\-/.]/g, '');

const ALIASES = {
  wellName: ['井名', 'well', 'wellname', 'wellid', 'borehole', 'uwi'],
  designName: ['方案名称', '方案名', 'designname', 'planname', 'scenario', 'case'],
  stageNumber: ['段号', '压裂段', '段数', 'stage', 'stageno', 'stagenumber', 'stageid', 'fracstage'],
  topMd: ['顶深mdm', '顶深md', '射孔顶深', '段顶深', 'topmd', 'mdtop', 'topmeasureddepth', 'startmd', 'frommd'],
  bottomMd: ['底深mdm', '底深md', '射孔底深', '段底深', 'bottommd', 'mdbase', 'base md', 'basemd', 'endmd', 'tomd'],
  clusterCount: ['簇数', '每段簇数', 'clusters', 'clustercount', 'numberofclusters', 'nclusters'],
  clusterSpacing: ['平均簇距m', '平均簇距', '簇间距', 'clusterspacing', 'avgclusterspacing', 'spacing'],
  fluidVolume: ['压裂液量m3', '压裂液量', '液量', 'fluidvolume', 'totalfluid', 'slurryvolume', 'watervolume'],
  proppantMass: ['支撑剂量t', '支撑剂量', '砂量', 'proppantmass', 'totalproppant', 'sandmass'],
  pumpRate: ['排量m3min', '排量', 'pumprate', 'injectionrate', 'rate'],
  fluidType: ['压裂液体系', '液体类型', 'fluidtype', 'fluidsystem'],
  proppantType: ['支撑剂类型', '支撑剂', 'proppanttype', 'proppant'],
  maxConcentration: ['最高砂浓度kgm3', '最高砂浓度', 'maxsandconcentration', 'maxproppantconcentration'],
  breakdownPressure: ['破裂压力mpa', '破裂压力', 'breakdownpressure', 'breakdownpressurempa'],
  isip: ['isipmpa', 'isip', '瞬时停泵压力', 'instantaneousshutinpressure'],
  clusterNumber: ['簇号', 'clusterno', 'clusternumber', 'clusterid'],
  clusterMd: ['簇位mdm', '簇位md', '簇深', 'clustermd', 'perforationmd', 'measureddepth', 'md'],
  clusterTop: ['簇顶深mdm', '簇顶深', 'clustertopmd', 'perftopmd'],
  clusterBottom: ['簇底深mdm', '簇底深', 'clusterbottommd', 'perfbottommd'],
} as const;

const rowMap = (row: SheetRow) => new Map(Object.entries(row).map(([key, value]) => [normalize(key), value]));
const pick = (row: SheetRow, aliases: readonly string[]) => {
  const map = rowMap(row);
  for (const alias of aliases) {
    const value = map.get(normalize(alias));
    if (value !== undefined && value !== '') return value;
  }
  return undefined;
};
const numberValue = (value: unknown) => {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : undefined;
};
const textValue = (value: unknown) => String(value ?? '').trim();
const average = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const parameterAlias: Record<string, keyof typeof ALIASES> = {
  方案名称: 'designName', designname: 'designName', planname: 'designName',
  井名: 'wellName', wellname: 'wellName', well: 'wellName',
  排量: 'pumpRate', pumprate: 'pumpRate',
  压裂液体系: 'fluidType', fluidtype: 'fluidType',
  支撑剂类型: 'proppantType', proppanttype: 'proppantType',
  最高砂浓度: 'maxConcentration', maxsandconcentration: 'maxConcentration',
};

export const parseFracturePlanWorkbook = (data: ArrayBuffer, filename = '导入方案.xlsx'): ImportedFracturePlan => {
  const workbook = XLSX.read(data, { type: 'array', cellDates: false });
  const sheets = workbook.SheetNames.map(name => ({
    name,
    rows: XLSX.utils.sheet_to_json<SheetRow>(workbook.Sheets[name], { defval: '', raw: true }),
  }));
  const warnings: string[] = [];
  const metadata: Partial<Record<keyof typeof ALIASES, unknown>> = {};

  for (const { rows } of sheets) {
    for (const row of rows) {
      const keys = Object.keys(row);
      const parameter = pick(row, ['参数字段', '参数', 'field', 'parameter', keys[0] ?? '']);
      const value = pick(row, ['值', 'value', keys[1] ?? '']);
      const aliasKey = parameterAlias[normalize(parameter)];
      if (aliasKey && value !== undefined && value !== '') metadata[aliasKey] = value;
    }
  }

  const rawStages: Array<Record<string, unknown>> = [];
  const clustersByStage = new Map<number, number[]>();
  for (const { rows } of sheets) {
    for (const row of rows) {
      const stageNumber = numberValue(pick(row, ALIASES.stageNumber));
      const top = numberValue(pick(row, ALIASES.topMd));
      const bottom = numberValue(pick(row, ALIASES.bottomMd));
      const clusterMd = numberValue(pick(row, ALIASES.clusterMd));
      if (stageNumber !== undefined && clusterMd !== undefined) {
        const key = Math.max(1, Math.round(stageNumber));
        clustersByStage.set(key, [...(clustersByStage.get(key) ?? []), clusterMd]);
      }
      if (top === undefined || bottom === undefined || bottom <= top) continue;
      const inferredStage = stageNumber ?? rawStages.length + 1;
      rawStages.push({
        stage_number: Math.max(1, Math.round(inferredStage)),
        top_perf: top,
        bottom_perf: bottom,
        fluid_volume: numberValue(pick(row, ALIASES.fluidVolume)) ?? 0,
        proppant_mass: numberValue(pick(row, ALIASES.proppantMass)) ?? 0,
        breakdown_pressure: numberValue(pick(row, ALIASES.breakdownPressure)) ?? null,
        isip: numberValue(pick(row, ALIASES.isip)) ?? null,
        _cluster_count: numberValue(pick(row, ALIASES.clusterCount)),
        _cluster_spacing: numberValue(pick(row, ALIASES.clusterSpacing)),
        _pump_rate: numberValue(pick(row, ALIASES.pumpRate)),
        _fluid_type: textValue(pick(row, ALIASES.fluidType)),
        _proppant_type: textValue(pick(row, ALIASES.proppantType)),
        _max_concentration: numberValue(pick(row, ALIASES.maxConcentration)),
        _well_name: textValue(pick(row, ALIASES.wellName)),
        _design_name: textValue(pick(row, ALIASES.designName)),
      });
    }
  }

  const uniqueStages = [...new Map(rawStages
    .sort((a, b) => Number(a.stage_number) - Number(b.stage_number))
    .map(stage => [Number(stage.stage_number), stage])).values()];
  if (!uniqueStages.length) throw new Error('未识别到有效分段。请至少提供段号、顶深 MD 和底深 MD 三列。');

  const stageClusterDesign = uniqueStages.map(stage => {
    const stageNumber = Number(stage.stage_number);
    const positions = [...new Set(clustersByStage.get(stageNumber) ?? [])].sort((a, b) => a - b);
    const spacing = numberValue(stage._cluster_spacing)
      ?? (positions.length > 1 ? average(positions.slice(1).map((value, index) => value - positions[index])) : 0);
    const clusterCount = positions.length || numberValue(stage._cluster_count) || 0;
    return {
      stage_number: stageNumber,
      cluster_per_stage: Math.max(0, Math.round(clusterCount)),
      cluster_spacing: Number(spacing.toFixed(2)),
      cluster_positions_md: positions.map(value => Number(value.toFixed(2))),
      source: positions.length ? 'excel_cluster_coordinates' : 'excel_stage_table',
    };
  });
  const missingClusters = stageClusterDesign.filter(row => !row.cluster_per_stage).length;
  if (missingClusters) warnings.push(`${missingClusters} 个压裂段未提供簇数或簇位，可进入分段分簇页面补充。`);

  const numbers = (key: string) => uniqueStages.map(stage => numberValue(stage[key])).filter((value): value is number => value !== undefined);
  const firstText = (key: string) => uniqueStages.map(stage => textValue(stage[key])).find(Boolean) ?? '';
  const planName = textValue(metadata.designName) || firstText('_design_name') || filename.replace(/\.(xlsx|xls|csv)$/i, '');
  const clusterCounts = stageClusterDesign.map(row => row.cluster_per_stage).filter(Boolean);
  const clusterSpacings = stageClusterDesign.map(row => row.cluster_spacing).filter(Boolean);
  const stageLengths = uniqueStages.map(stage => Number(stage.bottom_perf) - Number(stage.top_perf));
  const pumpRates = numbers('_pump_rate');
  const concentrations = numbers('_max_concentration');
  const fluidVolumes = numbers('fluid_volume');
  const proppantMasses = numbers('proppant_mass');

  const stages = uniqueStages.map(({ _cluster_count, _cluster_spacing, _pump_rate, _fluid_type, _proppant_type, _max_concentration, _well_name, _design_name, ...stage }) => stage);
  return {
    design: {
      name: planName || 'Excel导入方案',
      stage_count: stages.length,
      cluster_per_stage: Math.round(average(clusterCounts)),
      cluster_spacing: Number(average(clusterSpacings).toFixed(2)),
      stage_spacing: Number(average(stageLengths).toFixed(2)),
      pump_rate: Number((pumpRates[0] ?? 0).toFixed(2)),
      total_fluid: Number(fluidVolumes.reduce((sum, value) => sum + value, 0).toFixed(2)),
      total_proppant: Number(proppantMasses.reduce((sum, value) => sum + value, 0).toFixed(3)),
      fluid_type: textValue(metadata.fluidType) || firstText('_fluid_type') || '未指定',
      proppant_type: textValue(metadata.proppantType) || firstText('_proppant_type') || '未指定',
      max_proppant_conc: Number((numberValue(metadata.maxConcentration) ?? Math.max(0, ...concentrations)).toFixed(2)),
      notes: `Excel通用模板导入；来源文件：${filename}`,
      is_active: false,
      design_data: {
        import_format: 'universal_excel_v1',
        source_filename: filename,
        well_name: textValue(metadata.wellName) || firstText('_well_name'),
        stage_cluster_design: stageClusterDesign,
      },
    },
    stages,
    warnings,
  };
};

export const downloadFracturePlanTemplate = () => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ['参数字段', '值', '单位', '说明'],
    ['方案名称', '示例井压裂方案', '', '可修改'],
    ['井名', '示例井', '', '支持 Well / Well Name / UWI'],
    ['压裂液体系', '低黏滑溜水', '', '可在分段表逐段覆盖'],
    ['支撑剂类型', '40/70目石英砂', '', '可在分段表逐段覆盖'],
    ['最高砂浓度', 300, 'kg/m3', '用于泵注级次设计'],
  ]), '方案信息');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ['井名', '方案名称', '段号', '顶深MD(m)', '底深MD(m)', '簇数', '平均簇距(m)', '压裂液量(m3)', '支撑剂量(t)', '排量(m3/min)', '压裂液体系', '支撑剂类型', '最高砂浓度(kg/m3)', '破裂压力(MPa)', 'ISIP(MPa)'],
    ['示例井', '示例井压裂方案', 1, 3500, 3580, 6, 12, 1600, 160, 16, '低黏滑溜水', '40/70目石英砂', 300, 65, 48],
    ['示例井', '示例井压裂方案', 2, 3580, 3660, 6, 12, 1650, 165, 16, '低黏滑溜水', '40/70目石英砂', 320, 66, 49],
  ]), '分段设计');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ['井名', '段号', '簇号', '簇位MD(m)', '簇顶深MD(m)', '簇底深MD(m)', '有效孔数'],
    ['示例井', 1, 1, 3508, 3507.5, 3508.5, 36],
    ['示例井', 1, 2, 3521, 3520.5, 3521.5, 36],
    ['示例井', 2, 1, 3588, 3587.5, 3588.5, 36],
  ]), '簇位');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ['标准含义', '可识别的商业软件/英文列名示例'],
    ['井名', 'Well, Well Name, Well ID, Borehole, UWI'],
    ['段号', 'Stage, Stage No, Stage Number, Frac Stage'],
    ['顶深MD', 'Top MD, MD Top, Start MD, From MD'],
    ['底深MD', 'Bottom MD, Base MD, End MD, To MD'],
    ['簇位MD', 'Cluster MD, Perforation MD, Measured Depth, MD'],
    ['簇数', 'Clusters, Cluster Count, Number of Clusters'],
    ['说明', 'Petrel、Techlog 等软件导出后，可将相应列保留或粘贴到模板；列顺序不限，表名不限。'],
  ]), '字段映射说明');
  XLSX.writeFile(workbook, '压裂方案通用导入模板.xlsx');
};
